import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fmt2, fmtPct, fmtPts, fmtK, SmartProfileReportCard } from '../../components/ui/shared';

// Virtual equity/curve now sourced from the backend (computeVirtualAlphaRank,
// shared with the analyst's own dashboard) via /api/public/analyst-signal-set/:setId/virtual-alpharank —
// no more local recomputation, so numbers always match the Analyst Dashboard exactly.
// VIRT_INITIAL kept as a display-only reference (fixed virtual start balance = $1000,
// matches vInitial in backend virtual_alpharank.go) — used by chart tooltips/DD peak, not for calculation.
const VIRT_INITIAL = 1000;

function APEquityChart({curve,roi}) {
  const [hover,setHover]=React.useState(null);
  const svgRef=React.useRef(null);
  if(!curve||curve.length<2) return <div style={{padding:40,textAlign:"center",color:"var(--text-faint)",fontSize:12}}>Not enough signals yet</div>;
  const W=900,H=200,PAD={top:14,right:16,bottom:28,left:64};
  const iW=W-PAD.left-PAD.right,iH=H-PAD.top-PAD.bottom;
  const vals=curve.map(p=>p.v);
  const minV=Math.min(...vals)*0.994,maxV=Math.max(...vals)*1.006;
  const xOf=i=>PAD.left+(i/Math.max(curve.length-1,1))*iW;
  const yOf=v=>PAD.top+iH-((v-minV)/(maxV-minV||1))*iH;
  const pathD=curve.map((p,i)=>`${i===0?"M":"L"}${xOf(i).toFixed(1)},${yOf(p.v).toFixed(1)}`).join(" ");
  const areaD=pathD+` L${xOf(curve.length-1).toFixed(1)},${yOf(minV).toFixed(1)} L${xOf(0).toFixed(1)},${yOf(minV).toFixed(1)} Z`;
  const col=roi>=0?"var(--accent)":"var(--danger)";
  const yTicks=Array.from({length:5},(_,i)=>minV+(maxV-minV)*(i/4));
  return (
    <div style={{position:"relative",width:"100%",aspectRatio:`${W}/${H}`,minHeight:120,maxHeight:260}}>
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{position:"absolute",top:0,left:0,width:"100%",height:"100%"}}
        onMouseMove={e=>{const r=svgRef.current?.getBoundingClientRect();if(!r)return;const x=(e.clientX-r.left)/r.width*W;const idx=Math.min(curve.length-1,Math.max(0,Math.round((x-PAD.left)/iW*(curve.length-1))));setHover({idx,x:xOf(idx),point:curve[idx]});}}
        onMouseLeave={()=>setHover(null)}>
        <defs><linearGradient id="ap-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={roi>=0?"#4e9af1":"var(--danger)"} stopOpacity="0.25"/><stop offset="100%" stopColor={roi>=0?"#4e9af1":"var(--danger)"} stopOpacity="0.02"/></linearGradient></defs>
        {yTicks.map((v,i)=>(<g key={i}><line x1={PAD.left} y1={yOf(v)} x2={W-PAD.right} y2={yOf(v)} stroke="var(--border-subtle)" strokeWidth="0.5" strokeDasharray="4 4"/><text x={PAD.left-4} y={yOf(v)+4} textAnchor="end" fontSize="10" fill="var(--text-faint)">${fmt2(v)}</text></g>))}
        <path d={areaD} fill="url(#ap-fill)"/>
        <path d={pathD} fill="none" stroke={col} strokeWidth="1.8"/>
        {hover&&<><line x1={hover.x} y1={PAD.top} x2={hover.x} y2={H-PAD.bottom} stroke={col} strokeWidth="0.8" opacity="0.4"/><circle cx={hover.x} cy={yOf(hover.point.v)} r="4" fill={col} stroke="var(--bg-surface)" strokeWidth="2"/></>}
      </svg>
      {hover&&hover.point&&(
        <div style={{position:"absolute",top:8,left:hover.idx>curve.length*0.6?"auto":(hover.x/W*100)+"%",right:hover.idx>curve.length*0.6?(100-hover.x/W*100)+"%":"auto",background:"var(--bg-elevated)",border:"1px solid var(--border)",borderRadius:"var(--radius-md)",padding:"8px 12px",fontSize:11,pointerEvents:"none",whiteSpace:"nowrap",zIndex:10}}>
          {hover.point.t&&<div style={{color:"var(--text-faint)",marginBottom:4}}>{new Date(hover.point.t).toLocaleDateString()}</div>}
          <div style={{fontWeight:700,color:col}}>Balance: ${fmt2(hover.point.v)}</div>
          <div style={{color:(hover.point.v-VIRT_INITIAL)>=0?"var(--success)":"var(--danger)",marginTop:2}}>P&L: {(hover.point.v-VIRT_INITIAL)>=0?"+$":"-$"}{fmt2(Math.abs(hover.point.v-VIRT_INITIAL))}</div>
        </div>
      )}
    </div>
  );
}

function APDrawdownChart({curve}) {
  const [hover,setHover]=React.useState(null);
  const svgRef=React.useRef(null);
  const ddSeries=React.useMemo(()=>{let peak=VIRT_INITIAL;return(curve||[]).map(p=>{if(p.v>peak)peak=p.v;const dd=peak>0?(peak-p.v)/peak*100:0;return{t:p.t,dd:-dd};});},[curve]);
  if(ddSeries.length<2)return null;
  const W=900,H=140,PAD={top:10,right:16,bottom:24,left:48};
  const iW=W-PAD.left-PAD.right,iH=H-PAD.top-PAD.bottom;
  const minDD=Math.min(...ddSeries.map(d=>d.dd),-1)*1.1;
  const xOf=i=>PAD.left+(i/Math.max(ddSeries.length-1,1))*iW;
  const yOf=v=>PAD.top+(v/minDD)*iH;
  const pathD=ddSeries.map((d,i)=>`${i===0?"M":"L"}${xOf(i).toFixed(1)},${yOf(d.dd).toFixed(1)}`).join(" ");
  const areaD=pathD+` L${xOf(ddSeries.length-1).toFixed(1)},${PAD.top} L${xOf(0).toFixed(1)},${PAD.top} Z`;
  const maxDDv=Math.abs(Math.min(...ddSeries.map(d=>d.dd)));
  const xTicks=ddSeries.length<=1?[0]:Array.from({length:Math.min(6,ddSeries.length)},(_,i)=>Math.round(i*(ddSeries.length-1)/(Math.min(6,ddSeries.length)-1)));
  const fmtD=str=>{if(!str)return"";const d=new Date(str);return `${d.getDate()}/${d.getMonth()+1}`;};
  return (
    <div style={{position:"relative",width:"100%",aspectRatio:`${W}/${H}`,minHeight:80,maxHeight:180}}>
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{position:"absolute",top:0,left:0,width:"100%",height:"100%"}}
        onMouseMove={e=>{const r=svgRef.current?.getBoundingClientRect();if(!r)return;const x=(e.clientX-r.left)/r.width*W;const idx=Math.min(ddSeries.length-1,Math.max(0,Math.round((x-PAD.left)/iW*(ddSeries.length-1))));setHover({idx,x:xOf(idx),point:ddSeries[idx]});}}
        onMouseLeave={()=>setHover(null)}>
        <defs><linearGradient id="ap-dd-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="rgba(239,68,68,0.35)"/><stop offset="100%" stopColor="rgba(239,68,68,0.05)"/></linearGradient></defs>
        {[-5,-10,-20,-30].filter(v=>v>=minDD).map(v=>(<g key={v}><line x1={PAD.left} y1={yOf(v)} x2={W-PAD.right} y2={yOf(v)} stroke="var(--border-subtle)" strokeWidth="0.5" strokeDasharray="4 4"/><text x={PAD.left-4} y={yOf(v)+4} textAnchor="end" fontSize="10" fill="var(--text-faint)">{v}%</text></g>))}
        <line x1={PAD.left} y1={PAD.top} x2={W-PAD.right} y2={PAD.top} stroke="var(--border)" strokeWidth="1"/>
        <path d={areaD} fill="url(#ap-dd-fill)"/>
        <path d={pathD} fill="none" stroke="var(--danger)" strokeWidth="1.5"/>
        {xTicks.map(i=><text key={i} x={xOf(i)} y={H-4} textAnchor="middle" fontSize="10" fill="var(--text-faint)">{fmtD(ddSeries[i]?.t)}</text>)}
        {hover&&<><line x1={hover.x} y1={PAD.top} x2={hover.x} y2={H-PAD.bottom} stroke="var(--danger)" strokeWidth="0.8" opacity="0.4"/><circle cx={hover.x} cy={yOf(hover.point.dd)} r="3" fill="var(--danger)" stroke="var(--bg-surface)" strokeWidth="2"/></>}
      </svg>
      {hover&&hover.point&&(
        <div style={{position:"absolute",top:4,left:hover.idx>ddSeries.length*0.6?"auto":(hover.x/W*100)+"%",right:hover.idx>ddSeries.length*0.6?(100-hover.x/W*100)+"%":"auto",background:"var(--bg-elevated)",border:"1px solid var(--border)",borderRadius:"var(--radius-md)",padding:"6px 10px",fontSize:11,pointerEvents:"none",whiteSpace:"nowrap",zIndex:10}}>
          {hover.point.t&&<div style={{color:"var(--text-faint)",marginBottom:2}}>{new Date(hover.point.t).toLocaleDateString()}</div>}
          <div style={{fontWeight:700,color:"var(--danger)"}}>DD: {Math.abs(hover.point.dd).toFixed(2)}%</div>
        </div>
      )}
    </div>
  );
}

function APMonthlyChart({trades}) {
  const now=new Date();
  const allData=React.useMemo(()=>{
    const map={};
    (trades||[]).forEach(t=>{
      const d=new Date(t.closeTime||0);
      const key=`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
      if(!map[key])map[key]={key,year:d.getFullYear(),profit:0,label:d.toLocaleString('en-US',{month:'short',year:'2-digit'})};
      map[key].profit+=t.pnl;
    });
    return Object.values(map).sort((a,b)=>a.key.localeCompare(b.key));
  },[trades]);
  const dataYears=[...new Set(allData.map(m=>m.year))].sort((a,b)=>b-a);
  const [selYear,setSelYear]=React.useState(()=>dataYears[0]||now.getFullYear());
  const data=allData.filter(m=>m.year===selYear);
  if(allData.length===0)return null;
  const maxAbs=Math.max(...data.map(m=>Math.abs(m.profit)),1);
  const sel={background:"var(--bg-elevated)",border:"1px solid var(--border)",color:"var(--text-main)",borderRadius:"var(--radius-md)",padding:"4px 8px",fontSize:12};
  return (
    <div style={{background:"var(--bg-surface)",border:"1px solid var(--border-subtle)",borderRadius:"var(--radius-xl)",padding:"20px 24px",marginBottom:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)"}}>Virtual Monthly Performance</div>
        <select value={selYear} onChange={e=>setSelYear(Number(e.target.value))} style={sel}>{dataYears.map(y=><option key={y} value={y}>{y}</option>)}</select>
      </div>
      <div style={{display:"flex",gap:6,alignItems:"flex-end",height:120,overflowX:"auto"}}>
        {data.map(m=>{const pos=m.profit>=0;const pct=Math.max((Math.abs(m.profit)/maxAbs)*100,8);return(
          <div key={m.key} style={{flex:"0 0 52px",width:52,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
            <div style={{fontSize:10,fontWeight:600,color:pos?"var(--success)":"var(--danger)"}}>{(pos?"+$":"-$")+fmt2(Math.abs(m.profit))}</div>
            <div style={{width:"75%",height:`${pct}%`,borderRadius:"4px 4px 0 0",background:pos?"var(--success)":"var(--danger)",opacity:0.9,minHeight:8}}/>
            <div style={{fontSize:10,color:"var(--text-faint)"}}>{m.label}</div>
          </div>
        );})}
      </div>
      <div style={{display:"flex",justifyContent:"space-between",marginTop:10,fontSize:11,color:"var(--text-faint)"}}>
        <span>Months: {data.length}</span><span>Profitable: {data.filter(m=>m.profit>0).length}</span><span>Loss: {data.filter(m=>m.profit<0).length}</span>
      </div>
    </div>
  );
}

function APWeeklyChart({trades}) {
  const now=new Date();
  const MN={1:"Jan",2:"Feb",3:"Mar",4:"Apr",5:"May",6:"Jun",7:"Jul",8:"Aug",9:"Sep",10:"Oct",11:"Nov",12:"Dec"};
  const allData=React.useMemo(()=>{
    const map={};
    (trades||[]).forEach(t=>{
      const d=new Date(t.closeTime||0);
      const onejan=new Date(d.getFullYear(),0,1);
      const week=Math.ceil((((d-onejan)/86400000)+onejan.getDay()+1)/7);
      const key=`${d.getFullYear()}-W${String(week).padStart(2,'0')}`;
      if(!map[key])map[key]={key,year:d.getFullYear(),month:d.getMonth()+1,profit:0,trades:0,label:`W${week}`,dateLabel:`${d.getDate()}/${d.getMonth()+1}`};
      map[key].profit+=t.pnl;map[key].trades+=1;
    });
    return Object.values(map).sort((a,b)=>a.key.localeCompare(b.key));
  },[trades]);
  const dataYears=[...new Set(allData.map(w=>w.year))].sort((a,b)=>b-a);
  const [selYear,setSelYear]=React.useState(()=>dataYears[0]||now.getFullYear());
  const [selMonth,setSelMonth]=React.useState(()=>now.getMonth()+1);
  const data=allData.filter(w=>w.year===selYear&&w.month===selMonth);
  if(allData.length===0)return null;
  const maxAbs=Math.max(...data.map(w=>Math.abs(w.profit)),1);
  const sel={background:"var(--bg-elevated)",border:"1px solid var(--border)",color:"var(--text-main)",borderRadius:"var(--radius-md)",padding:"4px 8px",fontSize:12};
  return (
    <div style={{background:"var(--bg-surface)",border:"1px solid var(--border-subtle)",borderRadius:"var(--radius-xl)",padding:"20px 24px",marginBottom:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)"}}>Virtual Weekly Performance</div>
        <div style={{display:"flex",gap:6}}>
          <select value={selMonth} onChange={e=>setSelMonth(Number(e.target.value))} style={sel}>{[1,2,3,4,5,6,7,8,9,10,11,12].map(m=><option key={m} value={m}>{MN[m]}</option>)}</select>
          <select value={selYear} onChange={e=>setSelYear(Number(e.target.value))} style={sel}>{dataYears.map(y=><option key={y} value={y}>{y}</option>)}</select>
        </div>
      </div>
      <div style={{display:"flex",gap:6,alignItems:"flex-end",height:100,overflowX:"auto"}}>
        {data.length===0?<div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",color:"var(--text-faint)",fontSize:12}}>No data for this period</div>:data.map(w=>{
          const pos=w.profit>=0;const pct=Math.max((Math.abs(w.profit)/maxAbs)*90,8);
          return(<div key={w.key} style={{flex:"0 0 44px",width:44,display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
            <div style={{fontSize:9,fontWeight:600,color:pos?"var(--success)":"var(--danger)"}}>{(pos?"+$":"-$")+fmt2(Math.abs(w.profit))}</div>
            <div style={{width:"75%",height:`${pct}%`,borderRadius:"3px 3px 0 0",background:pos?"var(--success)":"var(--danger)",opacity:0.9,minHeight:8}}/>
            <div style={{fontSize:9,color:"var(--text-faint)"}}>{w.label}</div>
            <div style={{fontSize:8,color:"var(--text-faint)",opacity:0.6}}>{w.dateLabel}</div>
          </div>);
        })}
      </div>
      <div style={{display:"flex",justifyContent:"space-between",marginTop:10,fontSize:11,color:"var(--text-faint)"}}>
        <span>Weeks: {data.length}</span><span>Profitable: {data.filter(w=>w.profit>0).length}</span><span>Trades: {data.reduce((s,w)=>s+w.trades,0)}</span>
      </div>
    </div>
  );
}

function APHeatmap({trades}) {
  const [view,setView]=React.useState("pnl");
  const {cells,dates}=React.useMemo(()=>{
    const map={};
    (trades||[]).forEach(t=>{
      const d=new Date(t.closeTime||0);
      const date=d.toISOString().slice(0,10);const hour=d.getUTCHours();const key=`${date}-${hour}`;
      if(!map[key])map[key]={date,hour,trades:0,pnl:0,wins:0};
      map[key].trades+=1;map[key].pnl+=t.pnl;if(t.pnl>0)map[key].wins+=1;
    });
    const dates=[...new Set(Object.values(map).map(c=>c.date))].sort();
    return {cells:map,dates};
  },[trades]);
  if(dates.length===0)return null;
  const HOURS=Array.from({length:24},(_,i)=>i);
  const DAY_NAMES=["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const getVal=c=>{if(!c||c.trades===0)return 0;if(view==="pnl")return c.pnl;if(view==="signals")return c.trades;return c.trades>0?c.wins/c.trades*100:0;};
  const allVals=Object.values(cells).map(c=>getVal(c)).filter(v=>v!==0);
  const maxVal=allVals.length?Math.max(...allVals.map(Math.abs)):1;
  const getColor=c=>{
    if(!c||c.trades===0)return"var(--bg-elevated)";const val=getVal(c);
    if(view==="signals"){const i=Math.min(val/maxVal,1);return `rgba(99,179,237,${0.15+i*0.7})`;}
    if(view==="winrate"){if(val>=60)return `rgba(34,197,94,${0.3+(val-60)/40*0.5})`;if(val>=40)return"rgba(245,158,11,0.3)";return"rgba(239,68,68,0.4)";}
    const i=Math.min(Math.abs(val)/maxVal,1);return val>=0?`rgba(34,197,94,${0.15+i*0.7})`:`rgba(239,68,68,${0.15+i*0.7})`;
  };
  const fmtVal=c=>{if(!c||c.trades===0)return"";if(view==="pnl")return(c.pnl>=0?"+":"")+fmt2(Math.abs(c.pnl));if(view==="signals")return c.trades;return Math.round(c.wins/c.trades*100)+"%";};
  const fmtDL=d=>{const dt=new Date(d);return `${dt.getDate()}/${dt.getMonth()+1}`;};
  const sel={background:"var(--bg-elevated)",border:"1px solid var(--border)",color:"var(--text-main)",borderRadius:"var(--radius-md)",padding:"4px 8px",fontSize:11};
  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
        <div style={{fontSize:11,color:"var(--text-faint)"}}>P&L by date (UTC hour) · hover for details</div>
        <select value={view} onChange={e=>setView(e.target.value)} style={sel}><option value="pnl">Net P&L</option><option value="signals">Signal Count</option><option value="winrate">Win Rate</option></select>
      </div>
      <div style={{overflowX:"auto",overflowY:"auto",maxHeight:350}}>
        <div style={{display:"grid",gridTemplateColumns:"36px repeat(24,1fr)",gap:2,minWidth:600}}>
          <div style={{fontSize:9,color:"var(--text-faint)",textAlign:"right",paddingRight:4}}>Date</div>
          {HOURS.map(h=><div key={h} style={{fontSize:9,color:"var(--text-faint)",textAlign:"center",paddingBottom:2}}>{h}</div>)}
          {dates.map(date=>{
            const dt=new Date(date);const dayName=DAY_NAMES[dt.getUTCDay()];
            return(<React.Fragment key={date}>
              <div style={{fontSize:9,color:"var(--text-faint)",display:"flex",flexDirection:"column",alignItems:"flex-end",justifyContent:"center",paddingRight:4,lineHeight:1.2}}>
                <span style={{fontWeight:600}}>{fmtDL(date)}</span><span style={{fontSize:8,opacity:0.6}}>{dayName}</span>
              </div>
              {HOURS.map(h=>{const c=cells[`${date}-${h}`];return(
                <div key={h} title={c&&c.trades>0?`${date} ${h}:00 UTC — ${c.trades} signals, P&L: ${(c.pnl>=0?"+$":"-$")+Math.abs(c.pnl).toFixed(2)}, Win: ${c.trades>0?Math.round(c.wins/c.trades*100):0}%`:""}
                  style={{height:20,minWidth:20,borderRadius:3,background:getColor(c),display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:600,color:c&&c.trades>0?"var(--text-main)":"transparent",border:"1px solid rgba(255,255,255,0.04)"}}>
                  {fmtVal(c)}
                </div>
              );})}
            </React.Fragment>);
          })}
        </div>
      </div>
      <div style={{fontSize:10,color:"var(--text-faint)",marginTop:6,textAlign:"center"}}>Hour (UTC) — hover cell for details</div>
    </div>
  );
}

function APBias({trades}) {
  const stats=React.useMemo(()=>{
    let buyT=0,sellT=0,buyP=0,sellP=0;
    (trades||[]).forEach(t=>{if((t.direction||"").toUpperCase()==="BUY"){buyT++;buyP+=t.pnl;}else{sellT++;sellP+=t.pnl;}});
    const total=buyT+sellT;
    return {buyT,sellT,buyP,sellP,total,buyPct:total?buyT/total*100:0,sellPct:total?sellT/total*100:0};
  },[trades]);
  if(stats.total===0)return null;
  return (
    <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",borderRadius:"var(--radius-xl)",padding:20,marginBottom:16}}>
      <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)",marginBottom:14}}>Long vs Short Bias (Virtual)</div>
      <div style={{display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
        <div style={{flex:1,minWidth:200}}>
          <div style={{display:"flex",height:10,borderRadius:6,overflow:"hidden",marginBottom:6}}>
            <div style={{width:`${stats.buyPct}%`,background:"var(--success)"}}/><div style={{width:`${stats.sellPct}%`,background:"var(--danger)"}}/>
          </div>
          <div style={{display:"flex",justifyContent:"space-between"}}>
            <span style={{fontSize:11,color:"var(--success)",fontWeight:600}}>Long {fmtPct(stats.buyPct)}</span>
            <span style={{fontSize:11,color:"var(--danger)",fontWeight:600}}>Short {fmtPct(stats.sellPct)}</span>
          </div>
        </div>
        <div style={{display:"flex",gap:16}}>
          <div><div style={{fontSize:10,color:"var(--text-faint)",marginBottom:2}}>Buy Signals</div><div style={{fontSize:13,fontWeight:700,color:"var(--success)"}}>{stats.buyT}</div><div style={{fontSize:10,color:"var(--text-faint)"}}>{(stats.buyP>=0?"+$":"-$")+fmt2(Math.abs(stats.buyP))} P&L</div></div>
          <div><div style={{fontSize:10,color:"var(--text-faint)",marginBottom:2}}>Sell Signals</div><div style={{fontSize:13,fontWeight:700,color:"var(--danger)"}}>{stats.sellT}</div><div style={{fontSize:10,color:"var(--text-faint)"}}>{(stats.sellP>=0?"+$":"-$")+fmt2(Math.abs(stats.sellP))} P&L</div></div>
        </div>
      </div>
    </div>
  );
}


const gradeColor = (g) => {
  if(!g) return "var(--text-muted)";
  if(g.startsWith("A")) return "var(--success)";
  if(g.startsWith("B")) return "var(--accent)";
  if(g.startsWith("C")) return "var(--warning)";
  return "var(--danger)";
};

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("access_token") || localStorage.getItem("token") || ""}`
});


// ─── Analyst Smart View — 4 Layer ────────────────────────────────────────────
const aGradeColor = g => !g?"var(--text-muted)":g.startsWith("A")?"var(--success)":g.startsWith("B")?"var(--accent)":g.startsWith("C")?"var(--warning)":"var(--danger)";

const aRiskMeta = r => {
  const m = {
    VERIFIED_SAFE:{label:"Very Low",emoji:"  ",color:"var(--success)"},
    LOW:          {label:"Low",     emoji:"  ",color:"var(--success)"},
    MEDIUM:       {label:"Medium",  emoji:"  ",color:"var(--warning)"},
    HIGH:         {label:"High",    emoji:"",color:"var(--warning)"},
    EXTREME:      {label:"Extreme", emoji:"  ",color:"var(--danger)"},
  };
  return m[r]||{label:r||"—",emoji:"",color:"var(--text-muted)"};
};

const aSevStyle = sev => {
  if(sev==="CRITICAL") return {color:"var(--danger)",bg:"rgba(239,68,68,0.10)",border:"rgba(239,68,68,0.25)"};
  if(sev==="MAJOR")    return {color:"var(--warning)",bg:"rgba(245,158,11,0.10)",border:"rgba(245,158,11,0.25)"};
  return {color:"var(--text-muted)",bg:"rgba(255,255,255,0.04)",border:"rgba(255,255,255,0.10)"};
};

function AnalystSmartView({ set, stats, metrics, pillars, flags, vResult, riskLevel, hasEnoughData, riskPct, setRiskPct, smartReport }) {
  const [showAdv, setShowAdv] = React.useState(false);

  const grade      = hasEnoughData ? (set.virtualAlphaGrade||set.alphaGrade||"?") : "?";
  const score      = hasEnoughData ? (set.virtualAlphaScore||set.alphaScore||0) : 0;
  const gc         = hasEnoughData ? aGradeColor(grade) : "var(--text-muted)";
  const roi        = vResult.roi||0;
  const maxDD      = vResult.maxDD||0;
  const winRate    = stats.winRate||0;
  const avgRR      = metrics.avgRR||0;
  const pf         = metrics.profitFactor||0;
  const rlMeta     = aRiskMeta(riskLevel);
  const activeFlags = (flags||[]).filter(f=>f.severity==="CRITICAL"||f.severity==="MAJOR");
  const minorFlags  = (flags||[]).filter(f=>f.severity!=="CRITICAL"&&f.severity!=="MAJOR");

  // Reliability = avg of P1-P7 scores
  const pillarScores = (pillars||[]).map(p=>Number(p.score||0));
  const reliability = pillarScores.length>0 ? pillarScores.reduce((a,b)=>a+b,0)/pillarScores.length : 0;
  const relColor = reliability>=70?"var(--success)":reliability>=50?"var(--accent)":reliability>=30?"var(--warning)":"var(--danger)";

  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>

      {/* LAYER 1 — First Impression */}
      <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",borderRadius:14,padding:"20px 24px"}}>
        <div style={{fontSize:10,color:"var(--text-faint)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:14}}>
          AlphaRank™ Signal Overview
        </div>
        <div style={{display:"flex",gap:20,alignItems:"center",flexWrap:"wrap"}}>
          {/* Grade */}
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4,minWidth:80}}>
            <div style={{width:72,height:72,borderRadius:"50%",border:`3px solid ${gc}`,
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:20,fontWeight:900,color:gc,background:"rgba(255,255,255,0.02)"}}>
              {grade}
            </div>
            <div style={{fontSize:17,fontWeight:900,color:gc}}>{hasEnoughData?score.toFixed(1):"—"}</div>
            <div style={{fontSize:9,color:"var(--text-faint)",textTransform:"uppercase",letterSpacing:"0.06em"}}>AlphaScore™</div>
            {!hasEnoughData && <div style={{fontSize:9,color:"var(--accent)",textAlign:"center"}}>{stats.closedSignals||0}/20 signals</div>}
          </div>

          <div style={{width:1,height:72,background:"var(--border)",flexShrink:0}}/>

          {/* Risk */}
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            <div style={{fontSize:10,color:"var(--text-faint)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>Signal Risk Profile</div>
            <div style={{display:"inline-flex",alignItems:"center",gap:6,padding:"5px 12px",borderRadius:20,
              background:`${rlMeta.color}18`,border:`1px solid ${rlMeta.color}44`}}>
              <span style={{fontSize:13}}>{rlMeta.emoji}</span>
              <span style={{fontSize:13,fontWeight:800,color:rlMeta.color}}>{rlMeta.label} Risk</span>
            </div>
            <div style={{fontSize:11,color:"var(--text-faint)"}}>Based on signal history & flag analysis</div>
          </div>

          <div style={{width:1,height:72,background:"var(--border)",flexShrink:0}}/>

          {/* Virtual ROI */}
          <div style={{display:"flex",flexDirection:"column",gap:4}}>
            <div style={{fontSize:10,color:"var(--text-faint)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>
              Virtual ROI
              <span style={{marginLeft:6,fontSize:9,padding:"1px 6px",borderRadius:10,
                background:"rgba(78,154,241,0.12)",color:"var(--accent)",border:"1px solid rgba(78,154,241,0.25)"}}>
                SIMULATED
              </span>
            </div>
            <div style={{fontSize:26,fontWeight:900,color:roi>=0?"var(--success)":"var(--danger)"}}>
              {roi>=0?"+":""}{roi.toFixed(1)}%
            </div>
            <div style={{fontSize:10,color:"var(--text-faint)"}}>at {riskPct}% risk/signal · $1,000 virtual start</div>
          </div>

          <div style={{width:1,height:72,background:"var(--border)",flexShrink:0}}/>

          {/* Flags summary */}
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            <div style={{fontSize:10,color:"var(--text-faint)",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>Signal Flags</div>
            {(flags||[]).length===0 ? (
              <span style={{fontSize:12,fontWeight:700,color:"var(--success)"}}> No flags</span>
            ) : (
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {activeFlags.length>0&&<span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:20,
                  background:"var(--flag-critical-bg)",color:"var(--flag-critical-text)",border:"1px solid var(--flag-critical-border)"}}>
                  {activeFlags.length} Critical/Major
                </span>}
                {minorFlags.length>0&&<span style={{fontSize:11,fontWeight:600,padding:"3px 10px",borderRadius:20,
                  background:"rgba(255,255,255,0.05)",color:"var(--text-faint)",border:"1px solid var(--border)"}}>
                  {minorFlags.length} Minor
                </span>}
              </div>
            )}
          </div>
        </div>
      </div>

      <SmartProfileReportCard smartReport={smartReport} />

      {/* ── SMART REPORT (consolidated) — Virtual DD + Flags + Reliability ── */}
      <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",borderRadius:14,padding:"20px 22px"}}>
        <div style={{fontSize:10,color:"var(--text-faint)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:16}}>
          Smart Report
        </div>

        {/* Row 1: Virtual DD + Active Signal Flags */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
          <div>
            <div style={{fontSize:10,color:"var(--text-faint)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>
              Virtual Drawdown State
              <span style={{marginLeft:8,fontSize:9,padding:"1px 6px",borderRadius:10,
                background:"rgba(78,154,241,0.12)",color:"var(--accent)",border:"1px solid rgba(78,154,241,0.25)"}}>
                SIMULATED
              </span>
            </div>
            {(()=>{
              const dd = Number(maxDD||0);
              const color = dd>=40?"var(--danger)":dd>=20?"var(--warning)":"var(--success)";
              const winR = Number(winRate||0);
              const trend = winR>=60?"↑ Strong Win Rate":winR>=50?"→ Moderate Win Rate":"↓ Below Average Win Rate";
              const trendColor = winR>=60?"var(--success)":winR>=50?"var(--text-muted)":"var(--danger)";
              return (
                <div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6}}>
                    <span style={{fontSize:22,fontWeight:900,color}}>{dd.toFixed(1)}%</span>
                    <span style={{fontSize:11,fontWeight:700,color:trendColor}}>{trend}</span>
                  </div>
                  <div style={{height:6,background:"rgba(255,255,255,0.06)",borderRadius:3,overflow:"hidden",marginBottom:4}}>
                    <div style={{height:"100%",width:`${Math.min(dd,100)}%`,background:color,borderRadius:3}}/>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:"var(--text-faint)",marginBottom:12}}>
                    <span>Virtual Max DD</span>
                    <span>based on {stats.closedSignals||0} closed signals</span>
                  </div>
                  <div style={{borderTop:"1px solid var(--border)",paddingTop:12,display:"flex",flexDirection:"column",gap:6}}>
                    {[
                      {l:"Win Rate",      v:`${winRate.toFixed(1)}%`,    c:"var(--text-main)"},
                      {l:"Avg R:R",       v:`1 : ${avgRR.toFixed(2)}`,   c:"var(--text-main)"},
                      {l:"Profit Factor", v:pf.toFixed(2),               c:"var(--text-main)"},
                      {l:"Net Pips",      v:(metrics.netPips>=0?"+":"")+fmt2(metrics.netPips||0), c:metrics.netPips>=0?"var(--success)":"var(--danger)"},
                    ].map(x=>(
                      <div key={x.l} style={{display:"flex",justifyContent:"space-between"}}>
                        <span style={{fontSize:11,color:"var(--text-faint)"}}>{x.l}</span>
                        <span style={{fontSize:12,fontWeight:700,color:x.c}}>{x.v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>

          <div>
            <div style={{fontSize:10,color:"var(--text-faint)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>
              Active Signal Flags
            </div>
            {activeFlags.length===0 ? (
              <div style={{display:"flex",flexDirection:"column",gap:6,paddingTop:4}}>
                <div style={{fontSize:13,fontWeight:700,color:"var(--success)"}}>No critical or major flags</div>
                <div style={{fontSize:11,color:"var(--text-faint)"}}>Signal history shows a clean risk profile</div>
              </div>
            ) : activeFlags.map((f,i)=>{
              const st=aSevStyle(f.severity);
              return (
                <div key={i} style={{background:st.bg,border:`1px solid ${st.border}`,borderRadius:8,padding:"10px 12px",marginBottom:6}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                    <span style={{fontSize:12,fontWeight:700,color:st.color}}>{f.soft_title||f.title||f.name}</span>
                    <span style={{fontSize:9,fontWeight:800,padding:"2px 6px",borderRadius:4,
                      background:`${st.color}22`,color:st.color}}>{f.severity}</span>
                  </div>
                  <div style={{fontSize:11,color:"var(--text-faint)",lineHeight:1.5}}>{f.soft_desc||f.desc||f.description}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Row 2: Reliability (compact) + Virtual Simulation disclaimer */}
        <div style={{marginTop:18,paddingTop:16,borderTop:"1px solid var(--border)",display:"flex",gap:16,alignItems:"stretch",flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"baseline",gap:8,minWidth:160}}>
            <span style={{fontSize:20,fontWeight:900,color:relColor}}>{reliability.toFixed(0)}<span style={{fontSize:11,color:"var(--text-faint)",fontWeight:600}}>/100</span></span>
            <div style={{display:"flex",flexDirection:"column"}}>
              <span style={{fontSize:11,fontWeight:700,color:"var(--text-muted)"}}>Reliability</span>
              <span style={{fontSize:10,color:"var(--text-faint)"}}>
                {reliability>=70?"Historically consistent signal quality":reliability>=50?"Moderate signal quality observed":reliability>=30?"Variable signal quality observed":"Limited signal history available"}
              </span>
            </div>
          </div>

          <div style={{flex:1,minWidth:280,background:"rgba(78,154,241,0.05)",border:"1px solid rgba(78,154,241,0.2)",borderRadius:10,padding:"14px 16px"}}>
            <div style={{fontSize:10,color:"var(--accent)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>
              ◆ Virtual Simulation — How It Works
            </div>
            <div style={{fontSize:12,color:"var(--text-muted)",lineHeight:1.7}}>
              This analyst does not manage a live trading account. All performance metrics are calculated from a
              virtual simulation using historical signal data — starting with a $1,000 virtual balance
              at <strong style={{color:"var(--text-main)"}}>{riskPct}% risk per signal</strong>.
            </div>
            <div style={{fontSize:11,color:"var(--text-faint)",marginTop:8,lineHeight:1.6}}>
              Virtual results are for informational and analytical purposes only. Past signal performance
              does not guarantee future results. Adjust simulation risk:
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8,marginTop:8}}>
              {[0.5,1,1.5,2,3].map(r=>(
                <button key={r} onClick={()=>setRiskPct(r)}
                  style={{padding:"3px 10px",fontSize:11,borderRadius:20,cursor:"pointer",fontWeight:700,
                    border:`1px solid ${riskPct===r?"var(--accent)":"var(--border)"}`,
                    background:riskPct===r?"var(--accent)":"transparent",
                    color:riskPct===r?"#fff":"var(--text-muted)"}}>
                  {r}%
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* LAYER 4 — Advanced expandable */}
      <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",borderRadius:14,overflow:"hidden"}}>
        <button onClick={()=>setShowAdv(v=>!v)}
          style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",
            padding:"14px 20px",background:"transparent",border:"none",cursor:"pointer",
            color:"var(--text-muted)",fontSize:13,fontWeight:700}}>
          <span>Advanced Analysis {showAdv?"▲":"▼"}</span>
          <span style={{display:"flex",alignItems:"center",gap:8,fontSize:11,fontWeight:400,color:"var(--text-faint)"}}>
            {(()=>{
              const critCount = (flags||[]).filter(f=>f.severity==="CRITICAL").length;
              const minorCount = (flags||[]).filter(f=>f.severity!=="CRITICAL"&&f.severity!=="MAJOR").length;
              if(critCount===0 && minorCount===0) return null;
              return (
                <span style={{display:"flex",gap:6}}>
                  {critCount>0 && <span style={{fontWeight:700,color:"var(--danger)"}}>{critCount} Critical</span>}
                  {minorCount>0 && <span style={{color:"var(--text-faint)"}}>{minorCount} Minor</span>}
                </span>
              );
            })()}
            <span>P1–P7 Pillars · All Flags · Signal Breakdown</span>
          </span>
        </button>

        {showAdv && (
          <div style={{padding:"0 20px 20px",borderTop:"1px solid var(--border)"}}>

            {/* P1-P7 */}
            {(pillars||[]).length>0 && (
              <div style={{marginTop:16,marginBottom:20}}>
                <div style={{fontSize:11,color:"var(--text-faint)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:12}}>AlphaRank™ Pillar Breakdown</div>
                {pillars.map(p=>(
                  <div key={p.code} style={{marginBottom:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                      <span style={{fontSize:12,fontWeight:600,color:"var(--text-muted)"}}>{p.code}: {p.name}</span>
                      <span style={{fontSize:12,fontWeight:800,color:"var(--text-main)"}}>{Number(p.score||0).toFixed(0)}</span>
                    </div>
                    <div style={{height:4,background:"rgba(255,255,255,0.06)",borderRadius:2,overflow:"hidden"}}>
                      <div style={{height:"100%",width:`${Math.min(p.score,100)}%`,
                        background:"var(--accent)",borderRadius:2,
                        opacity:0.35+Math.min(p.score,100)/100*0.65}}/>
                    </div>
                    {p.reason&&<div style={{fontSize:10,color:"var(--text-faint)",marginTop:2}}>{p.reason}</div>}
                  </div>
                ))}
              </div>
            )}

            {/* Signal Breakdown */}
            <div style={{marginBottom:20}}>
              <div style={{fontSize:11,color:"var(--text-faint)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>Signal Breakdown</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
                {[
                  {l:"Total Signals",    v:stats.totalSignals||0},
                  {l:"Closed",          v:stats.closedSignals||0},
                  {l:"Running",         v:stats.runningSignals||0},
                  {l:"Wins",            v:stats.wins||0,     c:"var(--success)"},
                  {l:"Losses",          v:(stats.closedSignals||0)-(stats.wins||0), c:"var(--danger)"},
                  {l:"Max Consec. Loss",v:metrics.maxConsecLoss||0, c:"var(--danger)"},
                  {l:"Avg/Month",       v:fmt2(metrics.avgSignalMonth||0)},
                  {l:"Avg TP (pts)",    v:"+"+fmt2(metrics.avgTP||0), c:"var(--success)"},
                  {l:"Cumulative R",    v:((metrics.cumulativeR||0)>=0?"+":"")+fmt2(metrics.cumulativeR||0),
                    c:(metrics.cumulativeR||0)>=0?"var(--success)":"var(--danger)"},
                ].map(x=>(
                  <div key={x.l} style={{background:"var(--bg-elevated)",borderRadius:8,padding:"10px 12px",border:"1px solid var(--border)"}}>
                    <div style={{fontSize:10,color:"var(--text-faint)",marginBottom:4}}>{x.l}</div>
                    <div style={{fontSize:15,fontWeight:800,color:x.c||"var(--text-main)"}}>{x.v}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* All flags */}
            {(flags||[]).length>0 && (
              <div>
                <div style={{fontSize:11,color:"var(--text-faint)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>All Signal Flags ({flags.length})</div>
                {flags.map((f,i)=>{
                  const st=aSevStyle(f.severity);
                  return (
                    <div key={i} style={{background:st.bg,border:`1px solid ${st.border}`,borderRadius:8,padding:"10px 12px",marginBottom:6}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                        <span style={{fontSize:12,fontWeight:700,color:st.color}}>{f.soft_title||f.title||f.name}</span>
                        <span style={{fontSize:9,fontWeight:800,padding:"2px 6px",borderRadius:4,
                          background:`${st.color}22`,color:st.color}}>{f.severity}</span>
                      </div>
                      <div style={{fontSize:11,color:"var(--text-faint)",lineHeight:1.5}}>{f.soft_desc||f.desc||f.description}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

export default function AnalystProfile() {
  const { setId }   = useParams();
  const navigate    = useNavigate();
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [tab, setTab]               = useState("summary");
  const [subscribedAccounts, setSubscribedAccounts] = useState([]); // array of {id, followerAccountId}
  const [subscribed, setSubscribed] = useState(false);
  const [subModal, setSubModal]     = useState(false);
  const [subBusy, setSubBusy]       = useState(false);
  const [investorOnlyModal, setInvestorOnlyModal] = useState(false);
  const [investorAccounts, setInvestorAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [execMode, setExecMode] = useState("AUTO");
  const userRole = localStorage.getItem("user_role") || "trader";
  const [toast, setToast]           = useState(null);
  const [riskPct, setRiskPct]       = useState(1.5);
  const [heatmapPeriod, setHeatmapPeriod] = useState("3M");
  const [period, setPeriod]         = useState("All");
  const [vaResult, setVaResult]     = useState(null);
  const [vaLoading, setVaLoading]   = useState(true);

  const showToast = (msg, ok=true) => { setToast({msg,ok}); setTimeout(()=>setToast(null),3000); };

  useEffect(()=>{
    if(!setId) return;
    fetch(`/api/public/analyst-profile/${setId}`, { headers: authHeader() })
      .then(r=>r.json())
      .then(d=>{
        if(d.ok) {
          setData(d);
          if(d.set && !d.set.allowAutoCopy && d.set.allowManualFollow) setExecMode("MANUAL");
        } else setError(d.error||"Not found");
      })
      .catch(()=>setError("Failed to load"))
      .finally(()=>setLoading(false));
    const token = localStorage.getItem("access_token")||localStorage.getItem("token");
    if(token) {
      fetch("/api/investor/analyst-subscriptions", { headers: authHeader() })
        .then(r=>r.json()).then(d=>{
          if(d.ok && Array.isArray(d.subscriptions)) {
            const subs = d.subscriptions.filter(s=>s.setId===setId && s.status==="ACTIVE");
            setSubscribedAccounts(subs);
            }
            setSubscribed(subs.length > 0);
        }).catch(()=>{});
    }
  },[setId]);

  useEffect(()=>{
    if(!setId) return;
    setVaLoading(true);
    fetch(`/api/public/analyst-signal-set/${setId}/virtual-alpharank?risk_pct=${riskPct}`)
      .then(r=>r.json())
      .then(d=>{ setVaResult(d.ok ? d.alphaRank : null); })
      .catch(()=>setVaResult(null))
      .finally(()=>setVaLoading(false));
  },[setId, riskPct]);

  const handleSubscribe = async () => {
    setSubBusy(true);
    const r = await fetch("/api/investor/analyst-subscribe",{
      method:"POST", headers:{...authHeader(),"Content-Type":"application/json"},
      body: JSON.stringify({ setId, executionMode: execMode, autoFollow: execMode==="AUTO", followerAccountId: execMode==="MANUAL" ? "" : selectedAccount }),
    }).then(r=>r.json());
    if(r.ok){ setSubscribed(true); setSubModal(false); showToast(execMode==="MANUAL" ? "Subscribed with Manual Follow!" : "Subscribed with Auto-Follow!"); }
    else showToast(""+(r.error||"Failed"), false);
    setSubBusy(false);
  };

  const openSubModal = () => {
    const token = localStorage.getItem("access_token")||localStorage.getItem("token")||"";
    Promise.all([
      fetch("/api/trader/accounts", { headers:{ Authorization:`Bearer ${token}` }}).then(r=>r.json()),
      fetch("/api/investor/analyst-subscriptions", { headers:{ Authorization:`Bearer ${token}` }}).then(r=>r.json()),
    ]).then(([accData, subData]) => {
      const allAccs = (accData.accounts||[]).filter(a=>a.status==="active");
      const activeSubs = (subData.subscriptions||[]).filter(s=>s.setId===setId && s.status==="ACTIVE");
      const subscribedIds = new Set(activeSubs.map(s=>s.followerAccountId));
      const availableAccs = allAccs.filter(a=>!subscribedIds.has(a.id));
      setInvestorAccounts(availableAccs);
      setSelectedAccount(availableAccs[0]?.id||"");
      setSubModal(true);
    });
  };

  const handleUnsubscribe = async () => {
    setSubBusy(true);
    const r = await fetch("/api/investor/analyst-unsubscribe",{
      method:"POST", headers:{...authHeader(),"Content-Type":"application/json"},
      body: JSON.stringify({ setId }),
    }).then(r=>r.json());
    if(r.ok){ setSubscribed(false); showToast("Unsubscribed"); }
    else showToast(""+(r.error||"Failed"), false);
    setSubBusy(false);
  };

  const S = { bg:"var(--bg)", card:"var(--bg-surface)", card2:"var(--bg-elevated)", border:"var(--border-subtle)",
              text:"var(--text-main)", muted:"var(--text-muted)", accent:"var(--accent)", green:"var(--success)", red:"var(--danger)" };
  const history = data?.history || [];
  const rawCurve = React.useMemo(
    ()=>(vaResult?.equityCurve||[]).map(p=>({t:p.time, v:p.equity})),
    [vaResult]
  );
  const filteredCurve = React.useMemo(()=>{
    if(period==="All"||rawCurve.length===0) return rawCurve;
    const days = {"1M":30,"3M":90,"6M":180,"1Y":365}[period]||90;
    const cutoff = Date.now()-days*86400000;
    const kept = rawCurve.filter(p=>p.t && new Date(p.t).getTime()>=cutoff);
    return kept.length>0 ? kept : rawCurve;
  },[rawCurve, period]);
  const vResult = {
    curve: filteredCurve,
    trades: vaResult?.trades || [],
    maxDD: vaResult?.maxDD || 0,
    netPnL: vaResult?.netPnL || 0,
    roi: vaResult?.roi || 0,
    balance: vaResult?.virtualBalance || 1000,
    peak: vaResult?.peakBalance || 1000,
  };

  if(loading) return (
    <div style={{minHeight:"100vh",background:S.bg,display:"flex",alignItems:"center",justifyContent:"center",color:S.muted,fontSize:14}}>
      Loading analyst profile...
    </div>
  );
  if(error) return (
    <div style={{minHeight:"100vh",background:S.bg,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:12}}>
      <div style={{color:S.red,fontSize:16,fontWeight:700}}>{error}</div>
      <button onClick={()=>navigate("/marketplace")} style={{padding:"8px 20px",borderRadius:8,border:`1px solid ${S.border}`,background:"transparent",color:S.muted,cursor:"pointer",fontSize:13}}>
        Back to Marketplace
      </button>
    </div>
  );

  const { set, stats, metrics, pillars, flags, smart_report } = data;
  const hasEnoughData = (stats.closedSignals || 0) >= 20;
  const gc = hasEnoughData ? gradeColor(set.virtualAlphaGrade||set.alphaGrade) : "var(--text-muted)";
  const flagCount = Array.isArray(flags) ? flags.filter(f=>f.severity==="MAJOR"||f.severity==="CRITICAL").length : 0;
  const alphaScore = set.alphaScore || 0;
  const riskLevel = alphaScore < 30 ? "EXTREME" : alphaScore < 50 || flagCount >= 3 ? "HIGH" :
    alphaScore < 70 || flagCount >= 2 ? "MEDIUM" : alphaScore >= 85 && flagCount === 0 ? "VERIFIED_SAFE" : "LOW";
  const riskTag = riskLevel==="VERIFIED_SAFE" ? "Safe Risk" : riskLevel==="LOW" ? "Low Risk" :
    riskLevel==="MEDIUM" ? "Medium Risk" : riskLevel==="HIGH" ? "High Risk" : "Extreme Risk";

  return (
    <div style={{minHeight:"100vh", background:S.bg, color:S.text, fontFamily:"Inter,sans-serif"}}>

      {/* Header */}
      <div style={{background:S.card, borderBottom:`1px solid ${S.border}`, padding:"20px 32px"}}>
        <div style={{maxWidth:1100, margin:"0 auto"}}>
          <button onClick={()=>navigate("/marketplace")}
            style={{background:"transparent",border:"none",color:S.muted,cursor:"pointer",fontSize:13,marginBottom:12,padding:0}}>
            Back to Marketplace
          </button>
          <div style={{display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap:16}}>
            <div style={{display:"flex", alignItems:"center", gap:16}}>
              <div style={{width:60,height:60,borderRadius:"50%",border:`3px solid ${gc}`,
                display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:20,fontWeight:900,color:gc,background:"rgba(255,255,255,0.04)",flexShrink:0}}>
                {hasEnoughData ? (set.virtualAlphaGrade||set.alphaGrade) : "?"}
              </div>
              <div>
                <div style={{fontSize:22, fontWeight:900}}>{set.name}</div>
                <div style={{fontSize:13, color:S.muted, marginTop:2}}>by {set.analystName}</div>
                <div style={{fontSize:11, color:S.muted, marginTop:1}}>Active since {set.createdAt?.slice(0,10)}</div>
              </div>
            </div>
            <div style={{display:"flex", alignItems:"center", gap:16, flexWrap:"wrap"}}>
              <div style={{textAlign:"right"}}>
                <div style={{display:"inline-block",padding:"2px 10px",borderRadius:6,
                  fontSize:11,fontWeight:700,background:"rgba(255,255,255,0.05)",
                  border:"1px solid rgba(255,255,255,0.1)",color:"var(--text-muted)"}}>
                  {riskTag}
                </div>
              </div>
              <div style={{textAlign:"center"}}>
                {hasEnoughData ? (
                  <>
                    <div style={{fontSize:32, fontWeight:900, color:gc, lineHeight:1}}>{(set.virtualAlphaScore||set.alphaScore)?.toFixed(1)}</div>
                    <div style={{fontSize:11, color:S.muted}}>AlphaScore™</div>
                  </>
                ) : (
                  <>
                    <div style={{fontSize:20, fontWeight:900, color:"var(--text-muted)", lineHeight:1}}>—</div>
                    <div style={{fontSize:11, color:S.muted}}>AlphaScore™</div>
                    <div style={{fontSize:10, color:"var(--accent)", marginTop:2}}>{stats.closedSignals||0}/20 signals</div>
                  </>
                )}
              </div>
              {subscribed ? (
                <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:6}}>
                  <div style={{display:"flex",flexWrap:"wrap",gap:4,justifyContent:"flex-end",marginBottom:6}}>
                    {subscribedAccounts.map(s=>(
                      <span key={s.id} style={{fontSize:11,padding:"2px 10px",borderRadius:4,
                        background:"rgba(29,209,161,0.15)",color:"var(--success)",fontWeight:700}}>
                         {s.followerAccountNumber||"Account"}
                      </span>
                    ))}
                  </div>
                  <button onClick={openSubModal} disabled={subBusy}
                    style={{padding:"8px 20px",borderRadius:8,border:"1px solid rgba(78,154,241,0.4)",
                      background:"rgba(78,154,241,0.1)",color:S.accent,fontSize:13,fontWeight:700,cursor:"pointer"}}>
                    + Add Another Account
                  </button>
                </div>
              ) : (
                <button onClick={()=>{ if(userRole!=="investor"){ setInvestorOnlyModal(true); } else { openSubModal(); } }} disabled={subBusy}
                  style={{padding:"10px 24px",borderRadius:8,border:"none",
                    background:S.accent,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer"}}>
                  {subBusy ? "Subscribing..." : "Copy Signal"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div style={{background:S.card2, borderBottom:`1px solid ${S.border}`, padding:"14px 32px"}}>
        <div style={{maxWidth:1100, margin:"0 auto",
          display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(100px,1fr))", gap:12}}>
          {[
            {l:"ROI",            v:(vResult.roi>=0?"+":"")+fmt2(vResult.roi)+"%",   c:vResult.roi>=0?"var(--success)":"var(--danger)"},
            {l:"Max DD",         v:fmt2(vResult.maxDD)+"%",                          c:vResult.maxDD>20?"var(--danger)":vResult.maxDD>10?"var(--warning)":"var(--success)"},
            {l:"Closed Signals", v:fmt2(stats.closedSignals||0),                    c:"var(--text-main)"},
            {l:"Win Rate",       v:fmtPct(stats.winRate||0),                        c:"var(--text-main)"},
            {l:"Avg R:R",        v:"1:"+fmt2(metrics.avgRR||0),                     c:"var(--text-main)"},
            {l:"Profit Factor",  v:fmt2(metrics.profitFactor||0),                   c:"var(--text-main)"},
            {l:"Followers",      v:fmt2(stats.subscribers||0),                      c:"var(--text-main)"},
          ].map(x=>(
            <div key={x.l} style={{textAlign:"center"}}>
              <div style={{fontSize:18, fontWeight:800, color:x.c}}>{x.v}</div>
              <div style={{fontSize:10, color:S.muted, marginTop:2}}>{x.l}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{maxWidth:1100, margin:"0 auto", padding:"24px 32px"}}>

        {/* Tabs */}
        <div style={{display:"flex", gap:4, marginBottom:24, borderBottom:`1px solid ${S.border}`}}>
          {[["summary","Overview"],["performance","Performance"],["statistics","Statistics"],["history","History ("+history.length+")"],["profile","Profile"]].map(([id,label])=>(
            <button key={id} onClick={()=>setTab(id)} style={{
              padding:"8px 18px", border:"none", cursor:"pointer", fontSize:13, fontWeight:700,
              background:"transparent", color: tab===id ? S.accent : S.muted,
              borderBottom: tab===id ? `2px solid ${S.accent}` : "2px solid transparent",
              marginBottom:-1,
            }}>{label}</button>
          ))}
        </div>

        {/* SUMMARY */}
        {tab==="summary" && (
          <AnalystSmartView
            set={set} stats={stats} metrics={metrics} pillars={pillars} flags={flags}
            vResult={vResult} riskLevel={riskLevel} hasEnoughData={hasEnoughData}
            riskPct={riskPct} setRiskPct={setRiskPct} smartReport={smart_report}
          />
        )}

        {/* STATISTICS */}
        {tab==="statistics" && (
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:12}}>
            {[
              {l:"Total Signals",    v:String(stats.totalSignals||0)},
              {l:"Closed Signals",   v:String(stats.closedSignals||0)},
              {l:"Wins",             v:String(stats.wins||0),          c:"var(--success)"},
              {l:"Losses",           v:String((stats.closedSignals-stats.wins)||0), c:"var(--danger)"},
              {l:"Win Rate",         v:fmtPct(stats.winRate||0),     c:"var(--text-main)"},
              {l:"Avg R:R",          v:"1:"+fmt2(metrics.avgRR||0)},
              {l:"Profit Factor",  v:fmt2(metrics.profitFactor||0),                           c:"var(--text-main)"},
              {l:"Cumulative R",     v:((metrics.cumulativeR||0)>=0?"+":"")+fmt2(metrics.cumulativeR||0), c:(metrics.cumulativeR||0)>=0?"var(--success)":"var(--danger)"},
              {l:"Total Pips Win",   v:"+"+fmtPts(metrics.totalPipsWin||0),  c:"var(--success)"},
              {l:"Total Pips Loss",  v:"-"+fmtPts(metrics.totalPipsLoss||0),  c:"var(--danger)"},
              {l:"Net Pips",       v:((metrics.netPips||0)>=0?"+":"")+fmtPts(Math.abs(metrics.netPips||0)), c:(metrics.netPips||0)>=0?"var(--success)":"var(--danger)"},
              {l:"Max Consec. Loss", v:fmt2(metrics.maxConsecLoss||0), c:"var(--danger)"},
              {l:"Avg TP (pts)",     v:"+"+fmtPts(metrics.avgTP||0), c:"var(--success)"},
              {l:"Avg SL (pts)",     v:"-"+fmtPts(metrics.avgSL||0), c:"var(--danger)"},
              {l:"Avg Signal/Month", v:Number(metrics.avgSignalMonth||0).toFixed(1)},
              {l:"Avg Signal/Week",  v:Number(metrics.avgSignalWeek||0).toFixed(1)},
              {l:"Subscribers",      v:fmt2(stats.subscribers||0)},
              {l:"AlphaScore",       v:hasEnoughData ? fmt2(set.alphaScore||0) : "—", c:hasEnoughData ? gradeColor(set.alphaGrade) : "var(--text-muted)"},
            ].map(x=>(
              <div key={x.l} style={{background:S.card, border:`1px solid ${S.border}`, borderRadius:10, padding:"14px 18px"}}>
                <div style={{fontSize:11, color:S.muted, marginBottom:6}}>{x.l}</div>
                <div style={{fontSize:20, fontWeight:800, color:x.c||S.text}}>{x.v}</div>
              </div>
            ))}
          </div>
        )}

        {/* HISTORY */}
        {tab==="performance" && (
          <div>

            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12,marginBottom:20}}>
              {[
                {l:"Balance",  v:"$"+fmt2(vResult.balance),                                     c:"var(--text-main)"},
                {l:"Net P&L",          v:(vResult.netPnL>=0?"+$":"-$")+fmt2(Math.abs(vResult.netPnL)),  c:vResult.netPnL>=0?"var(--success)":"var(--danger)"},
                {l:"ROI",      v:(vResult.roi>=0?"+":"")+fmt2(vResult.roi)+"%",                 c:vResult.roi>=0?"var(--success)":"var(--danger)"},
                {l:"Max DD",     v:fmt2(vResult.maxDD)+"%",                                        c:vResult.maxDD>20?"var(--danger)":vResult.maxDD>10?"var(--warning)":"var(--success)"},
                {l:"Closed Signals",   v:fmt2(history.length),                                          c:"var(--text-main)"},
                {l:"Win Rate",         v:fmtPct(stats.winRate||0),                                      c:"var(--text-main)"},
                {l:"Profit Factor",    v:fmt2(metrics.profitFactor||0),                                 c:"var(--text-main)"},
                {l:"Avg R:R",          v:"1:"+fmt2(metrics.avgRR||0),                                   c:"var(--text-main)"},
              ].map(x=>(
                <div key={x.l} style={{background:"var(--bg-surface)",border:"1px solid var(--border-subtle)",borderRadius:"var(--radius-lg)",padding:"14px 18px"}}>
                  <div style={{fontSize:10,color:"var(--text-faint)",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>{x.l}</div>
                  <div style={{fontSize:18,fontWeight:700,color:x.c}}>{x.v}</div>
                </div>
              ))}
            </div>
            {/* Simulate Risk */}
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16,padding:"10px 16px",background:"var(--bg-surface)",borderRadius:"var(--radius-lg)",border:"1px solid var(--border-subtle)"}}>
              <span style={{fontSize:12,color:"var(--text-muted)",fontWeight:600}}>SIMULATE RISK PER TRADE:</span>
              {[0.5,1.5,3.0].map(r=>(
                <button key={r} onClick={()=>setRiskPct(r)} style={{padding:"4px 12px",fontSize:12,borderRadius:20,border:`1px solid ${riskPct===r?"var(--accent)":"var(--border)"}`,background:riskPct===r?"var(--accent)":"transparent",color:riskPct===r?"#fff":"var(--text-muted)",cursor:"pointer",fontWeight:riskPct===r?700:400}}>
                  {r===1.5?`${r}% (default)`:r+"%"}
                </button>
              ))}
              <input type="number" min="0.1" max="10" step="0.1" value={riskPct} onChange={e=>setRiskPct(Number(e.target.value))}
                style={{width:60,padding:"4px 8px",fontSize:12,borderRadius:6,border:"1px solid var(--border)",background:"var(--bg-main)",color:"var(--text-main)",textAlign:"center"}}/>
              <span style={{fontSize:12,color:"var(--text-muted)"}}>%</span>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:16}}>
              <span style={{fontSize:11,fontWeight:700,color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:0.6,marginRight:4}}>Period:</span>
              {["1M","3M","6M","1Y","All"].map(p=>(
                <button key={p} onClick={()=>setPeriod(p)} style={{padding:"3px 10px",fontSize:11,borderRadius:20,border:`1px solid ${period===p?"var(--accent)":"var(--border)"}`,background:period===p?"var(--accent)":"transparent",color:period===p?"#fff":"var(--text-muted)",cursor:"pointer"}}>{p}</button>
              ))}
            </div>
            <div style={{background:"var(--bg-surface)",border:"1px solid var(--border-subtle)",borderRadius:"var(--radius-xl)",padding:"20px 24px",marginBottom:16}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)"}}>Virtual Equity Growth Curve</div>
                  <div style={{fontSize:10,color:"var(--text-faint)",marginTop:2}}>${"$"}1,000 start · {riskPct}% risk/trade (compounding)</div>
                </div>
                <span style={{fontSize:12,fontWeight:700,padding:"2px 8px",borderRadius:"var(--radius-sm)",background:vResult.roi>=0?"var(--success-soft)":"var(--danger-soft)",color:vResult.roi>=0?"var(--success)":"var(--danger)"}}>
                  {vResult.roi>=0?"+":""}{fmt2(vResult.roi)}%
                </span>
              </div>
              <APEquityChart curve={vResult.curve} roi={vResult.roi}/>
              <div style={{display:"flex",gap:16,marginTop:12,paddingTop:12,borderTop:"1px solid var(--border-subtle)",flexWrap:"wrap"}}>
                {[
                  {label:"Start",   value:"$1,000.00"},
                  {label:"Current", value:"$"+fmt2(vResult.balance),            color:vResult.roi>=0?"var(--success)":"var(--danger)"},
                  {label:"Peak",    value:"$"+fmt2(vResult.peak||vResult.balance)},
                  {label:"Net P&L", value:(vResult.netPnL>=0?"+$":"-$")+fmt2(Math.abs(vResult.netPnL)), color:vResult.roi>=0?"var(--success)":"var(--danger)"},
                  {label:"Signals", value:history.length+" closed"},
                ].map(s=>(
                  <div key={s.label}>
                    <div style={{fontSize:10,color:"var(--text-faint)",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:2}}>{s.label}</div>
                    <div style={{fontSize:13,fontWeight:700,color:s.color||"var(--text-main)"}}>{s.value}</div>
                  </div>
                ))}
                {vaResult?.equityCurve?.length>1&&(()=>{ const ec=vaResult.equityCurve; const first=new Date(ec[0].time); const last=new Date(ec[ec.length-1].time); const d=vaResult.daysActive||Math.round((last-first)/(1000*60*60*24)); const fmt=dt=>`${dt.getDate()}/${dt.getMonth()+1}/${dt.getFullYear()}`; return <div style={{marginLeft:"auto"}}><div style={{fontSize:10,color:"var(--text-faint)",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:2}}>Trading Period</div><div style={{fontSize:11,fontWeight:600,color:"var(--text-muted)"}}>{fmt(first)} → {fmt(last)} <span style={{color:"var(--accent)"}}>({d} days)</span></div></div>; })()}
              </div>
            </div>
            <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",borderRadius:"var(--radius-xl)",padding:20,marginBottom:16}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)"}}>Virtual Drawdown — Underwater Curve</div>
                <span style={{fontSize:12,fontWeight:700,padding:"2px 8px",borderRadius:"var(--radius-sm)",background:"var(--danger-soft)",color:vResult.maxDD>20?"var(--danger)":vResult.maxDD>10?"var(--warning)":"var(--success)"}}>Max DD: {fmt2(vResult.maxDD)}%</span>
              </div>
              <APDrawdownChart curve={vResult.curve}/>
            </div>
            <APMonthlyChart trades={vResult.trades}/>
            <APWeeklyChart trades={vResult.trades}/>
            <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",borderRadius:"var(--radius-xl)",padding:20,marginBottom:16}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)"}}>Virtual Trading Hours Heatmap</div>
                <div style={{display:"flex",gap:4}}>
                  {["1M","3M","6M","1Y","All"].map(p=>(
                    <button key={p} onClick={()=>setHeatmapPeriod(p)} style={{padding:"3px 10px",fontSize:11,borderRadius:20,border:`1px solid ${heatmapPeriod===p?"var(--accent)":"var(--border)"}`,background:heatmapPeriod===p?"var(--accent)":"transparent",color:heatmapPeriod===p?"#fff":"var(--text-muted)",cursor:"pointer"}}>{p}</button>
                  ))}
                </div>
              </div>
              <APHeatmap trades={vResult.trades.filter(t=>{
                if(heatmapPeriod==="All") return true;
                const days={"1M":30,"3M":90,"6M":180,"1Y":365}[heatmapPeriod]||90;
                return new Date(t.closeTime)>=new Date(Date.now()-days*86400000);
              })} hideBorder/>
            </div>
            <APBias trades={vResult.trades}/>
          </div>
        )}

        {tab==="history" && (
          <div>
            <div style={{fontSize:12, color:S.muted, marginBottom:14}}>
              Showing last {history.length} closed signals
            </div>
            {history.length === 0 ? (
              <div style={{textAlign:"center", padding:60, color:S.muted}}>No closed signals yet</div>
            ) : (
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%", borderCollapse:"collapse", fontSize:12}}>
                  <thead>
                    <tr style={{borderBottom:`1px solid ${S.border}`, color:S.muted}}>
                      {["#","Pair","Dir","Entry","SL","TP","R:R","Pips","Result","Open Time","Close Time"].map(h=>(
                        <th key={h} style={{padding:"8px 10px", textAlign:"left", fontWeight:600, fontSize:11}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((sig,i)=>{
                      const isWin = sig.result==="WIN";
                      return (
                        <tr key={sig.id} style={{borderBottom:`1px solid rgba(255,255,255,0.04)`,
                          background: i%2===0?"transparent":"rgba(255,255,255,0.01)"}}>
                          <td style={{padding:"8px 10px", color:S.muted}}>{sig.id}</td>
                          <td style={{padding:"8px 10px", fontWeight:700}}>{sig.pair}</td>
                          <td style={{padding:"8px 10px", color:S.accent, fontWeight:700}}>{sig.direction}</td>
                          <td style={{padding:"8px 10px"}}>{sig.entry}</td>
                          <td style={{padding:"8px 10px", color:S.muted}}>{sig.sl}</td>
                          <td style={{padding:"8px 10px", color:S.muted}}>{sig.tp}</td>
                          <td style={{padding:"8px 10px", fontWeight:700}}>1:{fmt2(sig.rr||0)}</td>
                          <td style={{padding:"8px 10px", fontWeight:700, color:isWin?"var(--success)":"var(--danger)"}}>
                          {isWin?"+":""}{(Math.abs(sig.pips||0)>=1000?((sig.pips||0)/1000).toFixed(1)+"K":(sig.pips||0).toFixed(1))}
                          </td>
                          <td style={{padding:"8px 10px"}}>
                            <span style={{padding:"2px 8px", borderRadius:4, fontSize:10, fontWeight:800,
                              background: isWin?"rgba(29,209,161,0.15)":"rgba(248,113,113,0.15)",
                              color: S.text}}>
                              {sig.result}
                            </span>
                          </td>
                          <td style={{padding:"8px 10px", color:S.muted}}>{sig.issuedAt?sig.issuedAt.replace("T"," ").replace(/\+00:00|\.000Z|Z$/,""):"-"}</td>
                          <td style={{padding:"8px 10px", color:S.muted}}>{sig.closedAt?sig.closedAt.replace("T"," ").replace(/\+00:00|\.000Z|Z$/,""):"-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* PROFILE TAB */}
      {tab==="profile" && (
        <div style={{maxWidth:1100,margin:"0 auto",padding:"24px 32px"}}>
        <div style={{background:S.card, border:`1px solid ${S.border}`, borderRadius:14, padding:28, maxWidth:700}}>
          <div style={{fontSize:14, fontWeight:800, marginBottom:20}}> Analyst Profile</div>
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:20}}>
            <div>
              <div style={{fontSize:11, color:S.muted, marginBottom:6}}>Display Name</div>
              <div style={{fontSize:15, fontWeight:700, color:S.text}}>{set.analystName || "-"}</div>
            </div>
            <div>
              <div style={{fontSize:11, color:S.muted, marginBottom:6}}>Signal Set</div>
              <div style={{fontSize:15, fontWeight:700, color:S.text}}>{set.name}</div>
            </div>
            <div>
              <div style={{fontSize:11, color:S.muted, marginBottom:6}}>Country / Region</div>
              <div style={{fontSize:15, fontWeight:700, color:S.text}}>
                {set.analystCountry ? " "+set.analystCountry : "-"}
              </div>
            </div>
            <div>
              <div style={{fontSize:11, color:S.muted, marginBottom:6}}>Active Since</div>
              <div style={{fontSize:15, fontWeight:700, color:S.text}}>{set.createdAt?.slice(0,10)}</div>
            </div>
            {set.market && (
              <div>
                <div style={{fontSize:11, color:S.muted, marginBottom:6}}>Market Focus</div>
                <div style={{fontSize:15, fontWeight:700, color:S.text}}>{set.market}</div>
              </div>
            )}
            {set.style && (
              <div>
                <div style={{fontSize:11, color:S.muted, marginBottom:6}}>Trading Style</div>
                <div style={{fontSize:15, fontWeight:700, color:S.text}}>{set.style}</div>
              </div>
            )}
          </div>
          {set.analystBio ? (
            <div>
              <div style={{fontSize:11, color:S.muted, marginBottom:8}}>Bio & Trading Style</div>
              <div style={{fontSize:13, color:S.text, lineHeight:1.7,
                background:"rgba(255,255,255,0.03)", borderRadius:8, padding:"14px 16px",
                border:`1px solid ${S.border}`}}>
                {set.analystBio}
              </div>
            </div>
          ) : (
            <div style={{fontSize:13, color:S.muted, textAlign:"center", padding:"40px 0"}}>
              This analyst has not filled in their profile yet.
            </div>
          )}
        </div></div>
      )}

      {/* Subscribe Account Modal */}
      {subModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:2000,
          display:"flex",alignItems:"center",justifyContent:"center"}}
          onClick={()=>setSubModal(false)}>
          <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",
            borderRadius:16,padding:32,width:440,maxWidth:"90vw"}}
            onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:16,fontWeight:800,color:"var(--text-main)",marginBottom:4}}>Subscribe to Analyst</div>
            <div style={{fontSize:12,color:"var(--text-muted)",marginBottom:20}}>
              {execMode==="MANUAL" ? "You'll receive signals to execute manually — no live account needed." : "Signals will be auto-executed on your selected account."}
            </div>
            {data?.set?.allowAutoCopy && data?.set?.allowManualFollow && (
              <div style={{display:"flex",gap:8,marginBottom:20}}>
                <button onClick={()=>setExecMode("AUTO")}
                  style={{flex:1,padding:"10px",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",
                    border:execMode==="AUTO"?"1px solid var(--accent)":"1px solid var(--border)",
                    background:execMode==="AUTO"?"var(--accent-soft)":"transparent",
                    color:execMode==="AUTO"?"var(--accent)":"var(--text-muted)"}}>
                  Auto-Copy
                </button>
                <button onClick={()=>setExecMode("MANUAL")}
                  style={{flex:1,padding:"10px",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",
                    border:execMode==="MANUAL"?"1px solid var(--accent)":"1px solid var(--border)",
                    background:execMode==="MANUAL"?"var(--accent-soft)":"transparent",
                    color:execMode==="MANUAL"?"var(--accent)":"var(--text-muted)"}}>
                  Follow Manually (${data.set.manualSubscriptionFee}/mo)
                </button>
              </div>
            )}
            {execMode==="AUTO" ? (
              investorAccounts.length > 0 ? (
                <>
                  <div style={{fontSize:12,color:"var(--text-muted)",marginBottom:6,fontWeight:600}}>Copy using account:</div>
                  <select value={selectedAccount} onChange={e=>setSelectedAccount(e.target.value)}
                    style={{width:"100%",padding:"10px 12px",borderRadius:8,
                      background:"var(--bg-elevated)",border:"1px solid var(--border)",
                      color:"var(--text-main)",fontSize:13,marginBottom:20}}>
                    {investorAccounts.map(a=>(
                      <option key={a.id} value={a.id}>
                        {a.account_number} — {a.broker||a.server||"Broker"} ({a.platform||"MT5"})
                      </option>
                    ))}
                  </select>
                </>
              ) : (
                <div style={{fontSize:12,color:"var(--danger)",marginBottom:20,
                  background:"rgba(248,113,113,0.1)",borderRadius:8,padding:"10px 12px"}}>
                  All your accounts are already subscribed to this signal set. Manage allocations in your Investor Dashboard.
                </div>
              )
            ) : (
              <div style={{fontSize:12,color:"var(--text-muted)",marginBottom:20,
                background:"var(--bg-elevated-soft)",borderRadius:8,padding:"10px 12px",border:"1px solid var(--border-subtle)"}}>
                Flat subscription of ${data?.set?.manualSubscriptionFee}/month — billed regardless of performance, no live account required.
              </div>
            )}
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>setSubModal(false)}
                style={{flex:1,padding:"10px",borderRadius:8,border:"1px solid rgba(255,255,255,0.1)",
                  background:"transparent",color:"var(--text-muted)",fontSize:13,cursor:"pointer"}}>
                Cancel
              </button>
              <button onClick={handleSubscribe} disabled={subBusy||(execMode==="AUTO"&&!selectedAccount)}
                style={{flex:2,padding:"10px",borderRadius:8,border:"none",
                  background:"var(--accent)",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",
                  opacity:(subBusy||(execMode==="AUTO"&&!selectedAccount))?0.6:1}}>
                {subBusy?"Subscribing...":(execMode==="MANUAL"?"Subscribe (Manual Follow)":"Subscribe with Auto-Follow")}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Investor Only Modal */}
      {investorOnlyModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:2000,
          display:"flex",alignItems:"center",justifyContent:"center"}}
          onClick={()=>setInvestorOnlyModal(false)}>
          <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",
            borderRadius:16,padding:32,width:420,maxWidth:"90vw",textAlign:"center"}}
            onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:32,marginBottom:12}}></div>
            <div style={{fontSize:18,fontWeight:800,color:"var(--text-main)",marginBottom:8}}>
              Investor Account Required
            </div>
            <div style={{fontSize:13,color:"var(--text-muted)",marginBottom:8}}>
              To subscribe to signal sets, you need an Investor account.
            </div>
            <div style={{fontSize:12,color:"var(--danger)",marginBottom:24,
              background:"rgba(248,113,113,0.1)",borderRadius:8,padding:"8px 12px"}}>
              You must register with a <strong>new email address</strong> to create an Investor account.
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"center"}}>
              <button onClick={()=>setInvestorOnlyModal(false)}
                style={{padding:"10px 20px",borderRadius:8,border:"1px solid rgba(255,255,255,0.1)",
                  background:"transparent",color:"var(--text-muted)",fontSize:13,cursor:"pointer"}}>
                Close
              </button>
              <button onClick={()=>window.location.href="/register?role=investor"}
                style={{padding:"10px 24px",borderRadius:8,border:"none",
                  background:"var(--accent)",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer"}}>
                Register as Investor
              </button>
            </div>
          </div>
        </div>
      )}



      {toast && (
        <div style={{position:"fixed", bottom:24, right:24, zIndex:2000,
          background: toast.ok ? S.green : S.red,
          color:"#fff", padding:"12px 20px", borderRadius:10,
          fontWeight:700, fontSize:13, boxShadow:"0 4px 20px rgba(0,0,0,0.3)"}}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
